<template>
    <div class="side-sticky-layout">
        <div class="side-sticky-layout__sidebar" :class="{'sidebar-opened': showSidebar}">
            <slot name="sidebar"></slot>
        </div>
        <div v-show="!showSidebar" class="side-sticky-layout__main">
            <slot name="main"></slot>
        </div>
        <div class="side-sticky-layout__sidebar-actions">
            <VJoyButton
                v-if="showSidebar"
                class="side-sticky-layout__sidebar-action-button"
                :icon="closeSidebarButtonIcon"
                :label="closeSidebarButtonLabel"
                @click="closeSidebar"
            />
            <VJoyButton
                v-else
                class="side-sticky-layout__sidebar-action-button"
                data-target-search-sidebar-action-button
                :icon="openSidebarButtonIcon"
                :label="openSidebarButtonLabel"
                @click="openSidebar"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';
    import {useMediaQuery} from '@malt/utils';
    import type {TJoyIconsNames} from '@maltjoy/core-vue';
    import {VJoyButton} from '@maltjoy/core-vue';

    defineProps<{
        openSidebarButtonIcon: TJoyIconsNames;
        openSidebarButtonLabel: string;
        closeSidebarButtonIcon: TJoyIconsNames;
        closeSidebarButtonLabel: string;
    }>();

    const showSidebar = ref(false);

    const {matchMedia} = useMediaQuery();
    const matchMediaSmOrLarger = computed(() => matchMedia.value.min.Xs);

    function openSidebar(): void {
        showSidebar.value = true;
        window.scroll({top: 0});
    }

    function closeSidebar(): void {
        showSidebar.value = false;
        window.scroll({top: 0});
    }

    // hide filters when screen size is not small anymore
    watch(matchMediaSmOrLarger, (newMatch, oldMatch) => {
        if (newMatch && newMatch !== oldMatch) {
            showSidebar.value = false;
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .side-sticky-layout {
        display: grid;
        grid-template-columns: 350px minmax(0, 1fr);

        &__sidebar {
            padding: var(--joy-core-spacing-6) 0 0 var(--joy-core-spacing-6);
        }

        &__main {
            padding: var(--joy-core-spacing-6) var(--joy-core-spacing-4) 0 var(--joy-core-spacing-6);
        }

        &__sidebar-action-button {
            width: 100%;
        }

        // on large screens, we do not display the "open sidebar" button because it is always displayed
        &__sidebar-actions {
            display: none;
        }

        // on small screens,
        @include joy.screen_sm {
            grid-template-columns: 1fr;

            // the "open sidebar" button must be displayed
            &__sidebar-actions {
                display: flex;
                justify-content: center;
                position: sticky;
                z-index: 1;
                background-color: var(--joy-color-background-default);
                bottom: 0;
                padding: var(--joy-core-spacing-6);
            }

            // the sidebar is hidden
            &__sidebar {
                display: none;
                padding: var(--joy-core-spacing-6) var(--joy-core-spacing-4) 0 var(--joy-core-spacing-4);

                // unless it requested to be opened
                &.sidebar-opened {
                    display: block;
                }
            }

            &__main {
                padding: var(--joy-core-spacing-6) var(--joy-core-spacing-4) 0 var(--joy-core-spacing-4);
            }
        }
    }
</style>
